import * as React from 'react'
import { useClientPursesList } from './hooks'
import { AggregatedTurnover, ClientPurseItem } from './types'
import { CButton, CCardBody } from '@coreui/react-pro'
import { Button, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { formatDigits } from '../../../libs/utils'
import { TableRowSelection } from 'antd/lib/table/interface'
import { ConnectPurseModal } from '../connectPurseModal/components'
import {
  belongsToLocalMethod,
  hasNoPurseDetails,
  removeDuplicateChildren,
  removeGroupElements,
  withLocalPurseGroups,
} from './utils'
import {
  clientGroupedPursesListMounted,
  unGroupClientPurseMounted,
} from './model'
import { useUnit } from 'effector-react'
import { $isRequestSuccess } from '../../../features/notifications'
import { useEffect, useMemo } from 'react'
import {
  CheckCircleOutlined,
  CloseSquareOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
} from '@ant-design/icons'

import styles from './styles.module.css'

type Props = {
  entityId: string
}

const aggregateByCurrency = (items: ClientPurseItem[]): AggregatedTurnover[] => {
  const map = items.reduce<Record<string, AggregatedTurnover>>(
    (acc, item) => {
    const { currency, deposit, withdrawal, total } = item.turnover

    if (currency === 'USD') {
      return acc
    }

    if (!acc[currency]) {
      acc[currency] = {
        currency,
        deposit: 0,
        withdrawal: 0,
        total: 0,
      }
    }

    acc[currency].deposit += deposit
    acc[currency].withdrawal += withdrawal
    acc[currency].total += total

    return acc
    },
    {}
  )

  return Object.values(map)
}

const formatBreakdown = (breakdown: string[]): string =>
  breakdown.length > 0 ? ` (${breakdown.join(', ')})` : ''

const formatAggregate = (
  amountUSD: number,
  currency: string,
  breakdown: AggregatedTurnover[],
  pick: (item: AggregatedTurnover) => number,
): string =>
  `${formatDigits(amountUSD)} ${currency}${formatBreakdown(
    breakdown.map((item) => `${formatDigits(pick(item))} ${item.currency}`),
  )}`

const formatLeaf = (amount: number, currency: string, amountUSD: number): string =>
  `${formatDigits(amount)} ${currency} ${
    amountUSD && currency !== 'USD' ? `(${amountUSD}$)` : ''
  }`

const rowBreakdown = (item: ClientPurseItem): AggregatedTurnover[] | null => {
  if (item.rowType === 'local_method_total') {
    return item.localMethodBreakdown ?? []
  }

  // У гейтвея часть оборота идёт без реквизитов, кошельки под ним его не покроют
  if (item.rowType === 'gateway') {
    return null
  }

  return item.children && item.children.length > 0
    ? aggregateByCurrency(item.children)
    : null
}

const amountColor = (amount: number): string =>
  amount > 0 ? 'green' : amount < 0 ? 'red' : 'black'

// Суммы приходят на строке LOCAL / страна, отдельную строку-итог под блоком добавляет фронт
const withLocalMethodTotals = (items: ClientPurseItem[]): ClientPurseItem[] =>
  items.map((item) => {
    if (item.rowType !== 'local_method') {
      return item
    }

    const gateways = item.children ?? []

    return {
      ...item,
      children: [
        ...gateways,
        {
          ...item,
          key: item.key * 1000000 + 999999,
          rowType: 'local_method_total',
          children: undefined,
          localMethodBreakdown: aggregateByCurrency(gateways),
        },
      ],
    }
  })

export const GroupedClientPursesList = ({ entityId }: Props) => {
  const { clientPurses, loading } = useClientPursesList(entityId)
  const isRequestSuccess = useUnit($isRequestSuccess)

  const [selectedRows, setSelectedRows] = React.useState<ClientPurseItem[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([])
  const [expandedRowKeys, setExpandedRowKeys] = React.useState<React.Key[]>([])

  const rows = useMemo(
    () => withLocalMethodTotals(withLocalPurseGroups(clientPurses)),
    [clientPurses],
  )

  const localMethodKeys = useMemo(
    () =>
      rows.filter((item) => item.rowType === 'local_method').map((item) => item.key),
    [rows],
  )

  const localGatewayKeys = useMemo(
    () =>
      rows.flatMap((item) =>
        (item.children ?? [])
          .filter(
            (child) =>
              child.rowType === 'gateway' && (child.children?.length ?? 0) > 0,
          )
          .map((child) => child.key),
      ),
    [rows],
  )

  // Строки, на которых рисуется нижняя граница рамки блока
  const localMethodLastRowKeys = useMemo(() => {
    const keys = new Set<React.Key>()

    rows.forEach((item) => {
      if (item.rowType !== 'local_method') {
        return
      }
      const children = item.children ?? []
      keys.add(children.length > 0 ? children[children.length - 1].key : item.key)
    })

    return keys
  }, [rows])

  // Блок Local Method не сворачивается
  useEffect(() => {
    setExpandedRowKeys((keys) =>
      Array.from(new Set([...keys, ...localMethodKeys, ...localGatewayKeys])),
    )
  }, [localMethodKeys, localGatewayKeys])

  const columns: ColumnsType<ClientPurseItem> = [
    {
      title: 'Active',
      render: (item: ClientPurseItem) =>
        item.rowType === 'local_method' ? (
          <strong style={{ whiteSpace: 'nowrap' }}>
            {`LOCAL / ${item.localMethodCountry || '?'}`}
          </strong>
        ) : null,
    },
    {
      title: 'Main in group',
      render: (item: ClientPurseItem) => {
        if (item.rowType === 'local_method_total') {
          return null
        }
        if (hasNoPurseDetails(item)) {
          return <strong>-</strong>
        }
        if (item.children && item.children.length > 0) {
          return null
        }
        return item.isMainInGroup ? (
          <CheckCircleOutlined className={'text-success'} />
        ) : (
          <CloseSquareOutlined className={'text-danger'} />
        )
      },
    },
    {
      title: 'Trust',
      render: (item: ClientPurseItem) => {
        if (item.rowType === 'local_method_total') {
          return null
        }
        return hasNoPurseDetails(item) ? (
          <div>-</div>
        ) : (
          <div>{item.isTrusted ? 'yes' : 'no'}</div>
        )
      },
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Group ID',
      render: (item: ClientPurseItem) => {
        if (item.rowType === 'local_method_total') {
          return null
        }
        return hasNoPurseDetails(item) ? <strong>-</strong> : item.purseGroupId
      },
    },
    {
      title: 'Bank',
      render: (item: ClientPurseItem) =>
        item.rowType === 'local_method_total' ? null : (
          <strong>{item.bankName ? item.bankName : '-'}</strong>
        ),
      defaultSortOrder: 'ascend',
      // sorter: (a, b) => a.gatewayName.localeCompare(b.gatewayName),
    },
    {
      title: 'Client Name',
      render: (item: ClientPurseItem) =>
        item.rowType === 'local_method_total' ? null : (
          <strong>{item.clientName ? item.clientName : '-'}</strong>
        ),
    },
    {
      title: 'Purse',
      render: (item: ClientPurseItem) =>
        item.rowType === 'local_method_total' ? null : (
          <strong>{item.mask ?? '-'}</strong>
        ),
    },
    {
      title: 'Owner Type',
      render: (item: ClientPurseItem) => {
        // Подпись итога — в одной колонке с общим Total под таблицей
        if (item.rowType === 'local_method_total') {
          return (
            <strong style={{ whiteSpace: 'nowrap' }}>Total (Local Method)</strong>
          )
        }
        const type = item.ownerClassificationStatus
        if (!type) return <strong>-</strong>
        if (type === 'OWNER_CLASSIFICATION_STATUS_OWNER') {
          return <Tag color="green-inverse">Own</Tag>
        }
        return <Tag color="volcano-inverse">Third-party</Tag>
      },
    },
    {
      title: 'Deposit',
      render: (item: ClientPurseItem) => {
        // Цифры шапки перенесены в строку-итог
        if (item.rowType === 'local_method') {
          return null
        }
        const breakdown = rowBreakdown(item)

        return (
          <span style={{ color: 'green' }}>
            {breakdown
              ? formatAggregate(
                  item.turnover.depositUSD,
                  item.turnover.currency,
                  breakdown,
                  (row) => row.deposit,
                )
              : formatLeaf(
                  item.turnover.deposit,
                  item.turnover.currency,
                  item.turnover.depositUSD,
                )}
          </span>
        )
      },
    },
    {
      title: 'Withdrawal',
      render: (item: ClientPurseItem) => {
        if (item.rowType === 'local_method') {
          return null
        }
        const breakdown = rowBreakdown(item)

        return (
          <span style={{ color: 'green' }}>
            {breakdown
              ? formatAggregate(
                  item.turnover.withdrawalUSD,
                  item.turnover.currency,
                  breakdown,
                  (row) => row.withdrawal,
                )
              : formatLeaf(
                  item.turnover.withdrawal,
                  item.turnover.currency,
                  item.turnover.withdrawalUSD,
                )}
          </span>
        )
      },
    },
    {
      title: 'Total',
      render: (item: ClientPurseItem) => {
        if (item.rowType === 'local_method') {
          return null
        }
        const breakdown = rowBreakdown(item)

        return (
          <span
            style={{
              color: amountColor(
                breakdown ? item.turnover.totalUSD : item.turnover.total,
              ),
            }}
          >
            {breakdown
              ? formatAggregate(
                  item.turnover.totalUSD,
                  item.turnover.currency,
                  breakdown,
                  (row) => row.total,
                )
              : formatLeaf(
                  item.turnover.total,
                  item.turnover.currency,
                  item.turnover.totalUSD,
                )}
          </span>
        )
      },
    },
  ]

  const calculateSummary = () => {
    let totalDeposit = 0
    let totalWithdrawal = 0
    let total = 0

    clientPurses.forEach((purse) => {
      totalDeposit += purse.turnover.depositUSD || 0
      totalWithdrawal += purse.turnover.withdrawalUSD || 0
      total += purse.turnover.totalUSD || 0
    })

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0}></Table.Summary.Cell>
        <Table.Summary.Cell index={1}></Table.Summary.Cell>
        <Table.Summary.Cell index={2}></Table.Summary.Cell>
        <Table.Summary.Cell index={3}></Table.Summary.Cell>
        <Table.Summary.Cell index={4}></Table.Summary.Cell>
        <Table.Summary.Cell index={5}></Table.Summary.Cell>
        <Table.Summary.Cell index={6}></Table.Summary.Cell>
        <Table.Summary.Cell index={7}></Table.Summary.Cell>
        <Table.Summary.Cell index={8} colSpan={1}>
          <strong>Total</strong>
        </Table.Summary.Cell>

        {/* Total Deposit */}
        <Table.Summary.Cell index={9}>
          <strong
            style={{
              color:
                totalDeposit > 0 ? 'green' : totalDeposit < 0 ? 'red' : 'black',
            }}
          >
            {totalDeposit.toFixed(2)} $
          </strong>
        </Table.Summary.Cell>

        {/* Total Withdrawal */}
        <Table.Summary.Cell index={10}>
          <strong
            style={{
              color: 'red',
            }}
          >
            {totalWithdrawal.toFixed(2)} $
          </strong>
        </Table.Summary.Cell>

        {/* Total */}
        <Table.Summary.Cell index={11}>
          <strong
            style={{ color: total > 0 ? 'green' : total < 0 ? 'red' : 'black' }}
          >
            {total.toFixed(2)} $
          </strong>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    )
  }

  const rowSelection: TableRowSelection<ClientPurseItem> = {
    checkStrictly: false,
    getCheckboxProps: (record) => ({
      disabled: belongsToLocalMethod(record),
    }),
    // В блоке Local Method чекбокс не нужен
    renderCell: (_checked, record, _index, originNode) =>
      belongsToLocalMethod(record) ? null : originNode,
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys)
      setSelectedRows(selectedRows)
    },
    onSelect: (record, selected, selectedRows) => {
      setSelectedRows(selectedRows)
    },
  }

  const isValidDisconnectPurses = useMemo(() => {
    if (selectedRows.length < 2) {
      return false
    }

    const resultRows = removeGroupElements(selectedRows)
    return resultRows.every(
      (item) =>
        item.purseGroupId === resultRows[0].purseGroupId && item.purseGroupId,
    )
  }, [selectedRows])

  useEffect(() => {
    if (isRequestSuccess) {
      clientGroupedPursesListMounted({
        clientId: entityId,
        orderBy: 'updatedAt',
        orderDirection: '-',
      })
      setSelectedRows([])
      setSelectedRowKeys([])
    }
  }, [isRequestSuccess])

  return (
    <CCardBody>
      <ConnectPurseModal
        purses={removeDuplicateChildren(selectedRows)}
        clientId={entityId}
      />
      <Button
        color="purple"
        variant="solid"
        style={{ marginLeft: 8 }}
        disabled={!isValidDisconnectPurses}
        onClick={() => {
          if (
            window.confirm(
              'Are you sure you want to unlink the selected wallets?',
            )
          ) {
            const resultRows = removeGroupElements(selectedRows)

            if (isValidDisconnectPurses) {
              unGroupClientPurseMounted({
                clientPursesIds: resultRows
                  .filter((item) => item.clientPurseId)
                  .map((item) => item.clientPurseId as number),
              })
            }
          }
        }}
      >
        Disconnect purses
      </Button>
      <Table
        dataSource={rows}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        columns={columns}
        summary={calculateSummary}
        pagination={false}
        rowSelection={{ ...rowSelection }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) =>
            setExpandedRowKeys(
              Array.from(new Set([...keys, ...localMethodKeys])),
            ),
          expandIcon: ({ expanded, onExpand, record }) => {
            if (record.rowType === 'local_method') {
              return null
            }
            if (!record.children?.length) {
              return (
                <span className="ant-table-row-expand-icon ant-table-row-expand-icon-spaced" />
              )
            }
            return expanded ? (
              <MinusSquareOutlined onClick={(e) => onExpand(record, e)} />
            ) : (
              <PlusSquareOutlined onClick={(e) => onExpand(record, e)} />
            )
          },
        }}
        rowClassName={(record) => {
          if (belongsToLocalMethod(record)) {
            return [
              styles.localMethodBlock,
              record.rowType === 'local_method' ? styles.localMethodFirst : '',
              localMethodLastRowKeys.has(record.key)
                ? styles.localMethodLast
                : '',
              record.rowType === 'gateway' && (record.children?.length ?? 0) > 0
                ? styles.tableRowAlert
                : '',
              record.rowType === 'gateway_purse'
                ? styles.tableRowChildrenAlert
                : '',
              record.rowType === 'local_method_total'
                ? styles.localMethodTotalRow
                : '',
            ]
              .filter(Boolean)
              .join(' ')
          }
          if (Boolean(record.purseGroupId) && !Boolean(record.clientPurseId)) {
            return styles.tableRowAlert
          }
          if (Boolean(record.clientPurseId) && Boolean(record.purseGroupId)) {
            return styles.tableRowChildrenAlert
          }
          return ''
        }}
      />
    </CCardBody>
  )
}
